import { useEffect, useState } from "react";
import { Layers, Camera, Palette, CheckCheck, AlertCircle, Wand2, Loader2, RefreshCw } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import StimulusCard from "@/components/StimulusCard";
import { operantColor } from "@/lib/operants";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function ConceptGroupsPage() {
  const [groups, setGroups] = useState(null);
  const [pairing, setPairing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [redrawing, setRedrawing] = useState({}); // { [concept_id]: true }

  const load = () => http.get("/concept-groups").then((r) => setGroups(r.data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const redrawIllustration = async (conceptId) => {
    setRedrawing((s) => ({ ...s, [conceptId]: true }));
    try {
      const r = await http.post(`/concepts/${encodeURIComponent(conceptId)}/regenerate-illustrations`, { style: "illustration" });
      toast.success(`Redrew ${r.data.regenerated} illustration${r.data.regenerated === 1 ? "" : "s"} for ${conceptId}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Regenerate failed");
    } finally {
      setRedrawing((s) => { const n = { ...s }; delete n[conceptId]; return n; });
    }
  };

  const bulkPair = async () => {
    if (!window.confirm("Generate illustrated (or real) twins for every concept missing one? This may take a minute.")) return;
    setPairing(true);
    setProgress(null);
    try {
      const start = await http.post("/concept-groups/bulk-pair");
      const jobId = start.data.job_id;
      if (!jobId) {
        toast.success(`${start.data.already_paired || 0} concepts already paired — nothing to generate`);
        setPairing(false);
        return;
      }
      setProgress({ percent: 0, created: 0, failed: 0, total: start.data.total });
      let poll;
      const tick = async () => {
        try {
          const r = await http.get(`/concept-groups/bulk-pair/${jobId}`);
          setProgress(r.data);
          if (r.data.status === "done") {
            clearInterval(poll);
            toast.success(`Created ${r.data.created} pairs${r.data.failed ? ` · ${r.data.failed} failed` : ""}`);
            setPairing(false);
            setTimeout(() => setProgress(null), 2500);
            load();
          }
        } catch {
          clearInterval(poll);
          setPairing(false);
        }
      };
      poll = setInterval(tick, 900);
      tick();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk pair failed");
      setPairing(false);
    }
  };

  if (groups === null) {
    return <div className="p-12 text-slate-500">Loading concept groups…</div>;
  }

  const readyCount = groups.filter((g) => g.ready_for_non_identical).length;

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-6xl">
      <PageHeader
        eyebrow="Non-identical matching"
        title="Concept groups"
        description="Cards sharing a concept ID form matching pairs. Pair a real photo with an illustrated version to teach generalization."
        actions={
          <Button
            onClick={bulkPair}
            disabled={pairing || !groups || groups.length === 0}
            className="rounded-full h-11 px-4"
            data-testid="bulk-pair-button"
          >
            {pairing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Pairing…</> :
              <><Wand2 className="w-4 h-4 mr-2" /> Auto-pair every concept</>}
          </Button>
        }
      />

      {progress && (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="bulk-pair-progress">
          <div className="flex items-center justify-between mb-2 text-xs">
            <div className="font-bold uppercase tracking-wider text-slate-500">Generating pairs…</div>
            <div className="tabular-nums text-slate-700 font-semibold">
              {progress.created + progress.failed} / {progress.total} · {progress.percent || 0}%
            </div>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${progress.percent || 0}%` }}
            />
          </div>
          {progress.failed > 0 && (
            <div className="mt-2 text-[11px] text-rose-600">{progress.failed} failed — will retry on next run</div>
          )}
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatTile icon={Layers}    label="Total concepts"  value={groups.length}       tone="slate" testId="concept-total" />
        <StatTile icon={CheckCheck} label="Pair-ready"     value={readyCount}          tone="emerald" testId="concept-ready" />
        <StatTile icon={Camera}    label="Real cards"      value={groups.reduce((s, g) => s + g.real_count, 0)} tone="sky" testId="concept-real" />
        <StatTile icon={Palette}   label="Illustrations"   value={groups.reduce((s, g) => s + g.illustration_count, 0)} tone="fuchsia" testId="concept-illus" />
      </div>

      {groups.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 sm:p-16 text-center bg-white">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
            <Layers className="w-6 h-6 text-slate-500" />
          </div>
          <h3 className="mt-4 font-display font-bold text-xl">No concept groups yet</h3>
          <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
            Add a <strong>Concept group</strong> value when creating a card (e.g. "apple"). Two or more cards
            sharing a concept unlock non-identical matching drills in Activities.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div
              key={g.concept_id}
              data-testid={`concept-group-${g.concept_id}`}
              className={`rounded-2xl border p-5 ${g.ready_for_non_identical ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Concept</div>
                  <div className="font-display text-2xl font-extrabold">{g.concept_id}</div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-sky-100 text-sky-700 font-bold">
                    {g.real_count} real
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 font-bold">
                    {g.illustration_count} illustration
                  </span>
                  {g.operants.map((op) => {
                    const oc = operantColor(op);
                    return (
                      <span key={op} className="px-2.5 py-1 rounded-full font-bold border"
                        style={{ background: oc.bg, borderColor: oc.border, color: oc.text }}>
                        {op}
                      </span>
                    );
                  })}
                  {g.ready_for_non_identical ? (
                    <span className="px-2.5 py-1 rounded-full bg-emerald-600 text-white font-bold inline-flex items-center gap-1">
                      <CheckCheck className="w-3 h-3" /> Pair-ready
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-bold inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Add 1+ more
                    </span>
                  )}
                  {g.illustration_count > 0 && (
                    <button
                      onClick={() => redrawIllustration(g.concept_id)}
                      disabled={!!redrawing[g.concept_id]}
                      className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-bold inline-flex items-center gap-1 hover:bg-slate-100 disabled:opacity-50"
                      data-testid={`redraw-${g.concept_id}`}
                      title="Regenerate the illustration for every card in this concept"
                    >
                      {redrawing[g.concept_id] ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Redrawing…</>
                      ) : (
                        <><RefreshCw className="w-3 h-3" /> Redraw</>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {g.cards.map((card) => (
                  <StimulusCard key={card.id} card={card} size="sm" testId={`concept-card-${card.id}`} showSubtext={false} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone, testId }) {
  const cls = {
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky:     "bg-sky-50 text-sky-700 border-sky-200",
    fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  }[tone];
  return (
    <div className={`rounded-xl border p-3.5 ${cls}`} data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 opacity-80" />
        <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      </div>
      <div className="mt-1.5 font-display text-2xl font-extrabold">{value}</div>
    </div>
  );
}
